"use client";

import { c as _c } from "react/compiler-runtime";
import { Briefcase, Building, ChevronsUpDown, Command, Folder, Globe, Landmark, Layers, type LucideIcon, Network, Plus, Shield, Users } from "lucide-react";
import React, { cache, useCallback, useMemo } from "react";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { EntityEditOrViewLink } from "@components/common/buttons/entity-edit-or-view-link";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@components/ui/dropdown-menu";
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@components/ui/sidebar";
import { Skeleton } from "@components/ui/skeleton";
import { getArrayElementByCalculateHashIndex } from "@lib/common";
import { setToCookie } from "@lib/cookies";
import { useAppDispatch, useAppSelector } from "@lib/redux/hooks";
import { selectDefaultWorkspaceId } from "@features/application/app-selectors";
import { setDefaultWorkspaceId } from "@features/application/app-slice";
import routes from "@features/routes";
import { DEFAULT_WORKSPACE_ID_COOKIE_KEY } from "@features/workspaces/workspaces-consts";
import { selectCurrentWorkspace, selectUserWorkspaces } from "@features/workspaces/workspaces-selectors";
const workspaceIcons: LucideIcon[] = [Building, Briefcase, Users, Globe, Shield, Layers, Network, Landmark, Folder, Command];

/**
 * Retrieves a workspace icon based on the given ID.
 *
 * The function calculates a hash value for the provided ID by summing
 * the character codes of each character in the string. This hash value
 * is then used to determine the corresponding icon from a predefined
 * list of workspace icons using a modulo operation.
 *
 * @param {string} id - The unique identifier for a workspace.
 */
const getWorkspaceIconById = cache((id: string) => getArrayElementByCalculateHashIndex(id, workspaceIcons) as LucideIcon);

/**
 * A React functional component responsible for rendering a UI element that allows workspaces to switch between multiple workspaces.
 * It provides functionality for displaying the current workspace, selecting a preferred workspace, and listing available workspaces.
 *
 * Core Features:
 * 1. Fetches and displays a list of workspaces through the API.
 * 2. Shows the current workspace with its details like name and description.
 * 3. Allows workspaces to switch the active workspace and update the preference via cookies and Redux state.
 * 4. Renders workspace-specific icons for visual identification.
 * 5. Displays loading indicators if the workspace data is not yet available.
 * 6. Includes options for adding a new workspace.
 *
 * Dependencies:
 * - Utilizes several hooks for state management, side effects, and routing.
 * - Leverages components from a UI library to provide dropdown and sidebar functionalities.
 * - Integrates translations for multi-language support.
 *
 * States:
 * - `preferWorkspaceId`: Stores the ID of the preferred workspace selected by the user.
 *
 * Side Effects:
 * - Automatically navigates the user to a dashboard page if the preferred workspace is selected as the default.
 *
 * Handlers:
 * - Contains logic for updating the default workspace both in the local state and in browser cookies.
 *
 * Hooks and Utilities:
 * - `useSidebar`: Provides context about the sidebar, like whether it is in a mobile state.
 * - `useTranslations`: Fetches translation strings for various UI elements.
 * - `useGetWorkspacesQuery`: Queries the list of workspaces via an API.
 * - `useAppDispatch`: Provides a dispatch for Redux actions.
 * - `useAppSelector`: Retrieves specific pieces of state from the Redux store.
 * - `useRouter`: Handles navigation between routes.
 *
 * Styling and Layout:
 * - Employs various styled elements and skeleton loaders to maintain an interactive and responsive design while data is being loaded.
 */
export const WorkspaceSwitcher = () => {
  const $ = _c(56);
  const {
    isMobile
  } = useSidebar();
  const t = useTranslations("features.workspaces.ui.workspace-switcher");
  const tDomain = useTranslations("features.workspaces.domain");
  const router = useRouter();
  const dispatch = useAppDispatch();
  const workspaces = useAppSelector(selectUserWorkspaces);
  const defaultWorkspaceId = useAppSelector(selectDefaultWorkspaceId);
  const currentWorkspace = useAppSelector(selectCurrentWorkspace);
  const isLoading = currentWorkspace === undefined;
  let t0;
  if ($[0] !== defaultWorkspaceId || $[1] !== dispatch || $[2] !== router || $[3] !== workspaces) {
    t0 = preferWorkspaceId => {
      if (preferWorkspaceId === defaultWorkspaceId) {
        return;
      }
      setToCookie(DEFAULT_WORKSPACE_ID_COOKIE_KEY, preferWorkspaceId).then(() => {
        dispatch(setDefaultWorkspaceId(preferWorkspaceId));
        const targetWorkspace = workspaces?.find(workspace => workspace.id === preferWorkspaceId);
        const idOrSlug = targetWorkspace?.slug ?? targetWorkspace?.id;
        if (idOrSlug) {
          router.push(routes.events.pages.workspace_events.path({
            idOrSlug
          }));
        }
      });
    };
    $[0] = defaultWorkspaceId;
    $[1] = dispatch;
    $[2] = router;
    $[3] = workspaces;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  const setCurrentWorkspaceHandler = t0;
  let t1;
  if ($[5] !== setCurrentWorkspaceHandler || $[6] !== workspaces) {
    t1 = workspaces?.map(workspace_0 => {
      const OrgIcon = getWorkspaceIconById(workspace_0.id);
      return <DropdownMenuItem key={workspace_0.id} onClick={() => setCurrentWorkspaceHandler(workspace_0.id)} className="w-full cursor-pointer gap-2 p-2"><div className="flex size-6 items-center justify-center rounded-md border"><OrgIcon className="size-3.5 shrink-0" /></div>{workspace_0.hint}<EntityEditOrViewLink className="ml-auto cursor-pointer" entity={workspace_0} size="icon" variant="outline" /></DropdownMenuItem>;
    });
    $[5] = setCurrentWorkspaceHandler;
    $[6] = workspaces;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const WorkspaceList = t1;
  let t2;
  if ($[8] !== currentWorkspace) {
    t2 = () => {
      if (!currentWorkspace?.id) {
        return null;
      }
      const CurrentWorkspaceIcon = getWorkspaceIconById(currentWorkspace.id);
      return <CurrentWorkspaceIcon className="size-4" />;
    };
    $[8] = currentWorkspace;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const renderCurrentWorkspaceIcon = t2;
  let t3;
  if ($[10] !== renderCurrentWorkspaceIcon) {
    t3 = renderCurrentWorkspaceIcon();
    $[10] = renderCurrentWorkspaceIcon;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== t3) {
    t4 = <div className="flex aspect-square size-8 items-center justify-center rounded-md bg-sidebar-primary text-sidebar-primary-foreground">{t3}</div>;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== isLoading) {
    t5 = isLoading && <div className="flex flex-col gap-2"><Skeleton className="h-3 w-full rounded-md bg-gray-200" /><Skeleton className="h-3 w-full rounded-md bg-gray-200" /></div>;
    $[14] = isLoading;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== currentWorkspace || $[17] !== t || $[18] !== workspaces?.length) {
    t6 = !currentWorkspace && workspaces?.length === 0 && <><span className="truncate font-medium">{t("noWorkspaces")}</span><span className="truncate text-xs">{t("noWorkspacesDescription")}</span></>;
    $[16] = currentWorkspace;
    $[17] = t;
    $[18] = workspaces?.length;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== currentWorkspace) {
    t7 = currentWorkspace && <><span className="truncate font-medium">{currentWorkspace?.hint ?? "-----"}</span><span className="truncate text-xs">slug: {currentWorkspace?.slug ?? currentWorkspace?.id ?? "-----"}</span></>;
    $[20] = currentWorkspace;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== t5 || $[23] !== t6 || $[24] !== t7) {
    t8 = <div className="grid flex-1 text-left text-sm leading-tight">{t5}{t6}{t7}</div>;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <ChevronsUpDown className="ml-auto" />;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== t4 || $[28] !== t8) {
    t10 = <SidebarMenuButton size="lg" className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground">{t4}{t8}{t9}</SidebarMenuButton>;
    $[27] = t4;
    $[28] = t8;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== isLoading || $[31] !== t10) {
    t11 = <DropdownMenuTrigger asChild={true} disabled={isLoading}>{t10}</DropdownMenuTrigger>;
    $[30] = isLoading;
    $[31] = t10;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  const t12 = isMobile ? "bottom" : "right";
  let t13;
  if ($[33] !== tDomain) {
    t13 = tDomain("workspace.label_plural");
    $[33] = tDomain;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== t13) {
    t14 = <DropdownMenuLabel className="text-xs text-muted-foreground">{t13}</DropdownMenuLabel>;
    $[35] = t13;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <DropdownMenuSeparator />;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== router) {
    t16 = () => {
      router.push(routes.workspaces.pages.workspace_create.path());
    };
    $[38] = router;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <div className="flex size-6 items-center justify-center rounded-md border bg-transparent"><Plus className="size-4" /></div>;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] !== t) {
    t18 = t("addWorkspace");
    $[41] = t;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[43] !== t18) {
    t19 = <div className="font-medium text-muted-foreground">{t18}</div>;
    $[43] = t18;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] !== t16 || $[46] !== t19) {
    t20 = <DropdownMenuItem className="cursor-pointer gap-2 p-2" onClick={t16}>{t17}{t19}</DropdownMenuItem>;
    $[45] = t16;
    $[46] = t19;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  let t21;
  if ($[48] !== WorkspaceList || $[49] !== t12 || $[50] !== t14 || $[51] !== t20) {
    t21 = <DropdownMenuContent className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-md" align="start" side={t12} sideOffset={4}>{t14}{WorkspaceList}{t15}{t20}</DropdownMenuContent>;
    $[48] = WorkspaceList;
    $[49] = t12;
    $[50] = t14;
    $[51] = t20;
    $[52] = t21;
  } else {
    t21 = $[52];
  }
  let t22;
  if ($[53] !== t11 || $[54] !== t21) {
    t22 = <SidebarMenu><SidebarMenuItem><DropdownMenu>{t11}{t21}</DropdownMenu></SidebarMenuItem></SidebarMenu>;
    $[53] = t11;
    $[54] = t21;
    $[55] = t22;
  } else {
    t22 = $[55];
  }
  return t22;
};